import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Calendar, MapPin, Phone, Mail, X, SlidersHorizontal,Eye, CalendarCheck, FileText, Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPatientsLibrary, getPatientById } from '../../redux/patientSlice';
import { getDoctorById } from '../../redux/doctorSlice';
import { getCountries } from '../../redux/masterSlice';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../../components/common/FilterPanel';
import CommonLoader from '../../components/CommonLoader';
import Pagination from '../../components/Pagination';
import PatientDetailsModal from '../../components/modals/PatientDetailsModal';
import BookAppointmentModal from '../../components/modals/BookAppointmentModal';
import { getDoctorMissingSetupItems } from '../../utils/doctorSetupStatus';
import errorimg from '../../assets/img/colorkit (1).svg';
import MasterLabel from '../../components/common/MasterLabel';
import { getFileURL } from '../../utils/fileURL';

const formatDob = (dob) => {
    if (!dob) return '-';
    let date;
    const str = String(dob).trim();
    if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) {
        date = new Date(str);
    } else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) {
        const parts = str.split(/[/-]/);
        date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
        date = new Date(str);
    }
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Dummy patient data
const dummyPatients = [
  {
    id: 'PT001',
    name: 'Rajesh Kumar',
    dob: '15/03/1985',
    gender: 'Male',
    address: '123, MG Road, Bangalore, Karnataka - 560001',
    phone: '+91 98765 43210',
    email: 'rajesh.kumar@email.com',
    photo: 'https://i.pravatar.cc/150?img=1',
    bloodGroup: 'O+',
    emergencyContact: '+91 98765 43211',
    registrationDate: '10/01/2023'
  },
  {
    id: 'PT002',
    name: 'Priya Sharma',
    dob: '22/07/1990',
    gender: 'Female',
    address: '456, Park Street, Mumbai, Maharashtra - 400001',
    phone: '+91 98765 43212',
    email: 'priya.sharma@email.com',
    photo: 'https://i.pravatar.cc/150?img=5',
    bloodGroup: 'A+',
    emergencyContact: '+91 98765 43213',
    registrationDate: '15/02/2023'
  },
  {
    id: 'PT003',
    name: 'Amit Patel',
    dob: '08/11/1988',
    gender: 'Male',
    address: '789, Sector 18, Noida, Uttar Pradesh - 201301',
    phone: '+91 98765 43214',
    email: 'amit.patel@email.com',
    photo: 'https://i.pravatar.cc/150?img=12',
    bloodGroup: 'B+',
    emergencyContact: '+91 98765 43215',
    registrationDate: '20/03/2023'
  },
  {
    id: 'PT004',
    name: 'Sneha Reddy',
    dob: '30/05/1992',
    gender: 'Female',
    address: '321, Jubilee Hills, Hyderabad, Telangana - 500033',
    phone: '+91 98765 43216',
    email: 'sneha.reddy@email.com',
    photo: 'https://i.pravatar.cc/150?img=9',
    bloodGroup: 'AB+',
    emergencyContact: '+91 98765 43217',
    registrationDate: '05/04/2023'
  },
  {
    id: 'PT005',
    name: 'Vikram Singh',
    dob: '14/09/1987',
    gender: 'Male',
    address: '654, Connaught Place, New Delhi - 110001',
    phone: '+91 98765 43218',
    email: 'vikram.singh@email.com',
    photo: 'https://i.pravatar.cc/150?img=15',
    bloodGroup: 'O-',
    emergencyContact: '+91 98765 43219',
    registrationDate: '12/05/2023'
  },
  {
    id: 'PT006',
    name: 'Anjali Desai',
    dob: '25/12/1991',
    gender: 'Female',
    address: '987, Koregaon Park, Pune, Maharashtra - 411001',
    phone: '+91 98765 43220',
    email: 'anjali.desai@email.com',
    photo: 'https://i.pravatar.cc/150?img=20',
    bloodGroup: 'A-',
    emergencyContact: '+91 98765 43221',
    registrationDate: '18/06/2023'
  },
  {
    id: 'PT007',
    name: 'Rahul Mehta',
    dob: '03/02/1989',
    gender: 'Male',
    address: '147, Salt Lake, Kolkata, West Bengal - 700064',
    phone: '+91 98765 43222',
    email: 'rahul.mehta@email.com',
    photo: 'https://i.pravatar.cc/150?img=33',
    bloodGroup: 'B-',
    emergencyContact: '+91 98765 43223',
    registrationDate: '22/07/2023'
  },
  {
    id: 'PT008',
    name: 'Kavita Nair',
    dob: '19/08/1993',
    gender: 'Female',
    address: '258, MG Road, Kochi, Kerala - 682016',
    phone: '+91 98765 43224',
    email: 'kavita.nair@email.com',
    photo: 'https://i.pravatar.cc/150?img=47',
    bloodGroup: 'AB-',
    emergencyContact: '+91 98765 43225',
    registrationDate: '08/08/2023'
  },
  {
    id: 'PT009',
    name: 'Suresh Iyer',
    dob: '11/04/1986',
    gender: 'Male',
    address: '369, Anna Nagar, Chennai, Tamil Nadu - 600040',
    phone: '+91 98765 43226',
    email: 'suresh.iyer@email.com',
    photo: 'https://i.pravatar.cc/150?img=52',
    bloodGroup: 'O+',
    emergencyContact: '+91 98765 43227',
    registrationDate: '14/09/2023'
  },
  {
    id: 'PT010',
    name: 'Meera Joshi',
    dob: '27/10/1994',
    gender: 'Female',
    address: '741, Civil Lines, Jaipur, Rajasthan - 302006',
    phone: '+91 98765 43228',
    email: 'meera.joshi@email.com',
    photo: 'https://i.pravatar.cc/150?img=68',
    bloodGroup: 'A+',
    emergencyContact: '+91 98765 43229',
    registrationDate: '30/10/2023'
  },
  {
    id: 'PT011',
    name: 'Arjun Verma',
    dob: '06/01/1990',
    gender: 'Male',
    address: '852, Sector 7, Chandigarh - 160007',
    phone: '+91 98765 43230',
    email: 'arjun.verma@email.com',
    photo: 'https://i.pravatar.cc/150?img=70',
    bloodGroup: 'B+',
    emergencyContact: '+91 98765 43231',
    registrationDate: '05/11/2023'
  },
  {
    id: 'PT012',
    name: 'Divya Menon',
    dob: '16/06/1992',
    gender: 'Female',
    address: '963, Banjara Hills, Hyderabad, Telangana - 500034',
    phone: '+91 98765 43232',
    email: 'divya.menon@email.com',
    photo: 'https://i.pravatar.cc/150?img=25',
    bloodGroup: 'O+',
    emergencyContact: '+91 98765 43233',
    registrationDate: '12/12/2023'
  }
];

export default function PatientLibrary() {
  const { t } = useTranslation(['patientlibrary', 'patientpage']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isInitialCall = useRef(true);
  const { patientsLibrary, patientsLibraryLoading, patientsLibraryTotal, patientsLibraryPage, patientsLibraryLimit, patientsLibraryTotalPages, error } = useSelector((state) => state.patient);
  const { countries } = useSelector((state) => state.master);

  // Get role and doctorId from localStorage
  const userRole = localStorage.getItem('Role');
  const doctorId = localStorage.getItem('ID') || '';

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchError, setSearchError] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    countryId: [],
    gender: []
  });
  const [filters, setFilters] = useState({
    countryId: [],
    gender: []
  });
  const [isPatientDetailsModalOpen, setIsPatientDetailsModalOpen] = useState(false);
  const [isBookAppointmentModalOpen, setIsBookAppointmentModalOpen] = useState(false);
  const [selectedPatientForDetails, setSelectedPatientForDetails] = useState(null);
  const [selectedPatientForBooking, setSelectedPatientForBooking] = useState(null);

  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const filterButtonRef = useRef(null);
  const filterRef = useRef(null);

  // Fetch countries on mount
  useEffect(() => {
    dispatch(getCountries({ status: 'active', limit: 100 }));
  }, [dispatch]);

  // Maximum length for search input
  const MAX_SEARCH_LENGTH = 50;

  // Validate search input
  const validateSearchInput = (value) => {
    // Remove any potentially dangerous characters
    const sanitized = value.replace(/[<>]/g, '');

    // Check length
    if (sanitized.length > MAX_SEARCH_LENGTH) {
      setSearchError(t('patientlibrary.search_exceed_limit', `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters`));
      return false;
    }

    // Check for only whitespace
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      setSearchError(t('patientlibrary.search_only_spaces', 'Search term cannot contain only spaces'));
      return false;
    }

    setSearchError('');
    return true;
  };

  // Debounce search term (only if no error)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only debounce if there's no error
    if (!searchError) {
      searchTimeoutRef.current = setTimeout(() => {
        // Trim the search term before setting it
        const trimmedSearch = searchTerm.trim();
        setDebouncedSearchTerm(trimmedSearch);
        setCurrentPage(1);
      }, 500);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchError]);

  // Sync tempFilters with filters when popup opens
  useEffect(() => {
    if (isFilterOpen) {
      setTempFilters({ ...filters });
    }
  }, [isFilterOpen]);

  // Close filter on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        // Reset tempFilters to current filters when closing without applying
        setTempFilters({ ...filters });
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filters]);

  // Refocus search input after loading
  useEffect(() => {
    const active = document.activeElement === inputRef.current;
    if (!active && inputRef.current && searchTerm.length > 0) {
      inputRef.current.focus();
    }
  }, [patientsLibraryLoading, searchTerm]);

  // Fetch patients library (only if no search error)
  useEffect(() => {
    const payload = {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm,
      doctorId: doctorId
    };
    if (filters.gender.length > 0) payload.gender = filters.gender.join(',');
    if (filters.countryId.length > 0) payload.countryId = filters.countryId.join(',');

    if (isInitialCall.current) {
      dispatch(getPatientsLibrary(payload));
      isInitialCall.current = false;
      return;
    }

    // Subsequent calls on dependency changes (only if no search error)
    if (!searchError) {
      dispatch(getPatientsLibrary(payload));
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filters, doctorId, dispatch, searchError]);

  const handlePageChange = (page, newItemsPerPage) => {
    if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;

    // Sanitize input - remove dangerous characters
    const sanitized = value.replace(/[<>]/g, '');
    const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;

    // Always update searchTerm so the input stays in sync
    setSearchTerm(finalValue);

    // Validate input
    validateSearchInput(finalValue);
  };

  // Function to refresh patient library
  const refreshPatientLibrary = () => {
    const payload = {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm,
      doctorId: doctorId
    };
    if (filters.gender.length > 0) payload.gender = filters.gender.join(',');
    if (filters.countryId.length > 0) payload.countryId = filters.countryId.join(',');
    dispatch(getPatientsLibrary(payload));
  };

  // Format patients with fullName
  const formattedPatients = (patientsLibrary || []).map((patient) => ({
    ...patient,
    fullName: `${patient.name || ''} ${patient.surname || ''}`.trim(),
    id: patient.patientId || patient._id,
    onViewClick: (pat) => {
      setSelectedPatientForDetails(pat);
      setIsPatientDetailsModalOpen(true);
      dispatch(getPatientById(pat.patientId || pat.id));
    },
    onBookClick: (pat) => {
      setSelectedPatientForBooking(pat);
      // Get patient details for booking
      dispatch(getPatientById(pat.patientId || pat.id));
      // Get current logged-in doctor info
      if (doctorId) {
        dispatch(getDoctorById(doctorId));
      }
      setIsBookAppointmentModalOpen(true);
    }
  }));

  const { selectedPatient, isLoading: patientLoading } = useSelector((state) => state.patient);
  const { selectedDoctor, loading: doctorLoading } = useSelector((state) => state.doctor);

  // Profile completion validation for doctors
  if (userRole === 'doctor') {
    // Get missing setup items using utility function
    const missingItemKeys = getDoctorMissingSetupItems(userRole, selectedDoctor);
    const missingItems = missingItemKeys.map(key => t(key));

    // If any required items are missing, show restriction page
    if (missingItems.length > 0) {
      return (
        <div data-testid="patient-library-profile-validation-container" className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
          <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
            <img data-testid="patient-library-profile-validation-icon" src={errorimg} alt={t('profileValidation.altText')} className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain" />
            <h2 data-testid="patient-library-profile-validation-title" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">{t('profileValidation.title')}</h2>
            <p data-testid="patient-library-profile-validation-message" className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
              {t('profileValidation.message')}
            </p>
            <div data-testid="patient-library-missing-items-section" className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4 sm:mb-6 md:mb-8">
              <p data-testid="patient-library-missing-items-label" className="text-xs sm:text-sm md:text-base text-gray-700 font-semibold mb-2">{t('profileValidation.missingItems')}:</p>
              <ul data-testid="patient-library-missing-items-list" className="list-disc list-inside space-y-1 text-xs sm:text-sm md:text-base text-gray-600">
                {missingItems.map((item, index) => (
                  <li data-testid={`patient-library-missing-item-row-${index}`} key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <button data-testid="patient-library-go-to-profile-button" onClick={() => navigate('/doctorProfile')} className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:from-[#3aa8a8] hover:to-[#2a7b7b] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 cursor-pointer">
              {t('profileValidation.goToProfile')}
            </button>
          </div>
        </div>
      );
    }
  }

  return (
    <div data-testid="patient-library-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
      {/* Header */}
      <div data-testid="patient-library-header-section" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 data-testid="patient-library-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
          {t('patientlibrary.title')}
        </h1>

        {/* Search and Filter */}
        <div data-testid="patient-library-search-filter-section" className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-80">
            <input
              data-testid="patient-library-search-input"
              ref={inputRef}
              type="text"
              placeholder={t('patientlibrary.search_placeholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              maxLength={50}
              disabled={patientsLibraryLoading}
              className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 ${searchError
                ? 'border-red-300 bg-red-50 focus:ring-red-500'
                : 'border-gray-300 focus:ring-[#4ABDBD]'
                } ${patientsLibraryLoading ? "opacity-70" : ""}`}
            />
            <Search data-testid="patient-library-search-icon" className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4ABDBD]" />
            {searchError && (
              <p data-testid="patient-library-search-error" className="text-xs text-red-600 mt-1 absolute top-full left-0">{searchError}</p>
            )}
          </div>

          {/* Filter Button + Popup */}
          <div className="relative w-full sm:w-auto">
            <button
              data-testid="patient-library-filter-button"
              ref={filterButtonRef}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="group flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
              aria-label={t('patientlibrary.filter_aria_label')}
              aria-expanded={isFilterOpen}
            >
              <SlidersHorizontal data-testid="patient-library-filter-icon" className="w-5 h-5" />
            </button>

            {isFilterOpen && (
              <FilterPopover ref={filterRef}>
                <FilterPanel
                    onClear={() => {
                      setTempFilters({ countryId: [], gender: [] });
                      setFilters({ countryId: [], gender: [] });
                      setCurrentPage(1);
                      setIsFilterOpen(false);
                    }}
                    onApply={() => {
                      setFilters(tempFilters);
                      setCurrentPage(1);
                      setIsFilterOpen(false);
                    }}
                    clearLabel={t('patientpage.clear', 'Clear')}
                    applyLabel={t('patientpage.apply', 'Apply')}
                >
                    <FilterSection title={t('patientpage.country', 'Country')} isActive={tempFilters.countryId.length > 0}>
                        <FilterCountryList
                            multiple
                            value={tempFilters.countryId}
                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                            countries={countries}
                            searchPlaceholder={t('patientpage.search_placeholder', 'Search...')}
                        />
                    </FilterSection>

                    <FilterSection title={t('patientpage.gender', 'Gender')} isActive={tempFilters.gender.length > 0}>
                        <FilterOptionList
                            multiple
                            value={tempFilters.gender}
                            onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                            options={[
                                { value: 'male', label: t('patientpage.male', 'Male') },
                                { value: 'female', label: t('patientpage.female', 'Female') },
                                { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                                { value: 'other', label: t('patientpage.other', 'Other') },
                            ]}
                        />
                    </FilterSection>
                </FilterPanel>
              </FilterPopover>
            )}
          </div>
        </div>
      </div>

      {/* Applied Filters Display */}
      {(filters.countryId.length > 0 || filters.gender.length > 0) && (
        <div data-testid="patient-library-applied-filters-section" className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="patient-library-applied-filters-label" className="text-sm font-semibold text-gray-700 mr-2">
              {t('patientpage.applied_filters', 'Applied Filters')}:
            </span>

            {filters.countryId.map((cid) => (
              <div key={`country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                <span className="text-xs text-gray-600">
                  <span className="font-semibold">{t('patientpage.country', 'Country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                </span>
                <button
                  onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            {filters.gender.map((g) => (
              <div key={`gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                <span className="text-xs text-gray-600">
                  <span className="font-semibold">{t('patientpage.gender', 'Gender')}:</span> {t(`patientpage.${g}`, g.charAt(0).toUpperCase() + g.slice(1))}
                </span>
                <button
                  onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            <button
              data-testid="patient-library-clear-all-filters-button"
              onClick={() => {
                setFilters({ countryId: [], gender: [] });
                setTempFilters({ countryId: [], gender: [] });
                setCurrentPage(1);
              }}
              className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
              {t('patientpage.clear_all', 'Clear All')}
            </button>
          </div>
        </div>
      )}

      {/* Patient Cards Grid */}
      {patientsLibraryLoading ? (
        <div data-testid="patient-library-loading-container" className="flex flex-col items-center justify-center py-12">
          <CommonLoader />
          <p data-testid="patient-library-loading-text" className="text-gray-500 text-lg mt-4">{t('patientlibrary.loading_patients', 'Loading patients...')}</p>
        </div>
      ) : formattedPatients.length === 0 ? (
        <div data-testid="patient-library-empty-container" className="text-center py-12">
          <p data-testid="patient-library-empty-text" className="text-gray-500 text-lg">{t('patientlibrary.no_patients_found', 'No patients found matching your search criteria.')}</p>
        </div>
      ) : (
        <>
          <div data-testid="patient-library-cards-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {formattedPatients.map((patient) => (
              <PatientCard key={patient.id} patient={patient} />
            ))}
          </div>
          {!patientsLibraryLoading && patientsLibraryTotal > 0 && (
            <div data-testid="patient-library-pagination-container" className="mt-6">
              <Pagination
                totalItems={patientsLibraryTotal}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                custombutton={formattedPatients.length === 0 ? 'disable' : ''}
              />
            </div>
          )}
        </>
      )}

      {/* Patient Details Modal */}
      <PatientDetailsModal
        isOpen={isPatientDetailsModalOpen}
        onClose={() => {
          setIsPatientDetailsModalOpen(false);
          setSelectedPatientForDetails(null);
        }}
        patient={selectedPatient}
        loading={patientLoading}
      />

      {/* Book Appointment Modal */}
      <BookAppointmentModal
        isOpen={isBookAppointmentModalOpen}
        onClose={() => {
          setIsBookAppointmentModalOpen(false);
          setSelectedPatientForBooking(null);
        }}
        doctor={selectedDoctor}
        loading={doctorLoading}
        onAppointmentBooked={() => {
          setIsBookAppointmentModalOpen(false);
          setSelectedPatientForBooking(null);
          refreshPatientLibrary();
        }}
      />
    </div>
  );
}

function PatientCard({ patient }) {
  const { t, i18n } = useTranslation('patientlibrary');
  const imageUrl = getFileURL(patient.image || patient.photo) || `https://ui-avatars.com/api/?name=${encodeURIComponent(patient.fullName || patient.name || 'Patient')}&size=150&background=4ABDBD&color=fff&bold=true`;

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return 'PT';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(patient.fullName || patient.name);

  return (
    <div data-testid={`patient-library-card-${patient.id}`} className="group relative flex flex-col justify-between bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 hover:shadow-2xl hover:border-[#4ABDBD]/60 transform hover:-translate-y-2">
      {/* Modern Teal Header with Avatar */}
      <div>
        <div className="relative p-5 overflow-hidden bg-gradient-to-br from-[#4ABDBD] via-[#3AA8A8] to-[#2D8F8F]">
          {/* Enhanced Decorative Pattern Overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }}></div>
          </div>

          {/* Gradient Shine Effect */}
          <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent"></div>

          <div className="relative flex items-start gap-4">
            {/* Large Avatar with Enhanced Gradient Design */}
            <div className="flex-shrink-0 relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-white/30 to-white/10 backdrop-blur-sm border-2 border-white/40 shadow-xl overflow-hidden flex items-center justify-center relative ring-2 ring-white/20">
                {/* Gradient Border Glow */}
                <div className="absolute -inset-0.5 rounded-2xl opacity-30 blur-sm bg-gradient-to-br from-[#4ABDBD] via-[#3AA8A8] to-[#2D8F8F]"></div>
                {imageUrl && !imageUrl.includes('ui-avatars') ? (
                  <>
                    <img
                      src={getFileURL(imageUrl)}
                      alt={patient.fullName || patient.name}
                      className="w-full h-full object-cover relative z-10"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="avatar-fallback hidden w-full h-full absolute inset-0 items-center justify-center text-white font-bold text-2xl relative z-10 bg-gradient-to-br from-[#4ABDBD]/40 to-[#3AA8A8]/40">
                      {initials}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl relative z-10 bg-gradient-to-br from-[#4ABDBD]/40 to-[#3AA8A8]/40">
                    {initials}
                  </div>
                )}
              </div>
            </div>

            {/* Patient Name and Info */}
            <div className="flex-1 min-w-0 pt-1">
              <h3 data-testid={`patient-library-card-name-${patient.id}`} className="font-bold text-xl text-white mb-3 truncate drop-shadow-lg">
                {patient.fullName || patient.name || '-'}
              </h3>

              {/* Gender, DOB, and Patient ID */}
              <div className="flex items-start gap-2 flex-wrap">
                {patient.gender && (
                  <div data-testid={`patient-library-card-gender-${patient.id}`} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <User className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold capitalize">
                      {t(`common.gender.${patient.gender.toLowerCase()}`, patient.gender)}
                    </span>
                  </div>
                )}

                {patient.dob && (
                  <div data-testid={`patient-library-card-dob-${patient.id}`} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <Calendar className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold truncate">
                      {formatDob(patient.dob)}
                    </span>
                  </div>
                )}

                {/* Patient ID */}
                {/* {patient.patientId && (
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <FileText className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold truncate">
                      {patient.patientId}
                    </span>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>

        {/* Clean White Body Section */}
        <div className="px-6 py-4 bg-white">

          {/* Contact Information */}
          <div className="space-y-3">
            {patient.phone && (
              <div className='flex items-center justify-between'>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                  <span data-testid={`patient-library-card-phone-${patient.id}`} className="text-sm text-gray-700">{patient.phone}</span>
                </div>
                {/* Action Buttons */}
                <div className="flex gap-2 ">
                    <button
                      data-testid={`patient-library-view-button-${patient.id}`}
                      onClick={() => patient.onViewClick && patient.onViewClick(patient)}
                      className=" flex items-center justify-center gap-1.5 p-2 bg-[#4ABDBD]/10 backdrop-blur-sm px-2 py-1 rounded-xl border border-[#4ABDBD]/20 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium text-xs"
                    >
                      <Eye className="w-3.5 h-3.5 text-[#4ABDBD]" />
                      {/* View Details */}
                    </button>
                    <button
                      data-testid={`patient-library-book-button-${patient.id}`}
                      onClick={() => patient.onBookClick && patient.onBookClick(patient)}
                      className=" flex items-center justify-center gap-1.5 p-2 px-4 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium text-xs"
                    >
                      {/* <CalendarCheck className="w-3.5 h-3.5" /> */}
                      {t('patientlibrary.book', 'Book')}
                    </button>
                  </div>
              </div>
            )}
            {patient.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                <span data-testid={`patient-library-card-email-${patient.id}`} className="text-sm text-gray-700 truncate">{patient.email}</span>
              </div>
            )}

            {patient.country && (
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                <span data-testid={`patient-library-card-country-${patient.id}`} className="text-sm text-gray-700">
                  <MasterLabel type="masterCountry" id={patient.country?.countryId} field="countryName" fallback={patient.country?.countryName || patient.country || ''} />
                </span>
              </div>
            )}

            {/* Profile Status */}
            {patient.profileStatus && (
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                <span data-testid={`patient-library-card-profile-status-${patient.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg inline-block bg-[#4ABDBD]/10 text-[#4ABDBD]">
                  {patient.profileStatus}
                </span>
              </div>
            )}

            {/* Status */}
            {patient.status && (
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                <span data-testid={`patient-library-card-status-${patient.id}`} className="text-sm text-green-600">{patient.status}</span>
              </div>
            )}

            {/* Created Date */}
            {patient.createdAt && (
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                <span data-testid={`patient-library-card-created-${patient.id}`} className="text-sm text-gray-500">
                  {t('patientlibrary.created', 'Created')}: {new Date(patient.createdAt).toLocaleDateString(i18n.language || 'en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subtle Bottom Accent */}
      <div className="h-1 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#4ABDBD]"></div>

      {/* Save Icon - Bottom Right Corner */}
      {patient.alreadyMy && (
        <div data-testid={`patient-library-bookmark-container-${patient.id}`} className="absolute bottom-4 right-4 z-10 group/save">
          <Bookmark data-testid={`patient-library-bookmark-icon-${patient.id}`} className="w-8 h-8 text-[#ec4899] fill-[#ec4899] drop-shadow-lg transition-all duration-200" />
          {/* Tooltip */}
          <div data-testid={`patient-library-bookmark-tooltip-${patient.id}`} className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover/save:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg">
            {t('patientlibrary.already_in_my_patient', 'already in my patient')}
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
}

